import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PLAN_TERMS, type AdminSession, type CreateSessionResult } from '@wallflower/shared';
import { ApiError } from '../lib/api.ts';
import { accountApi, useAccount, useCapabilities } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';
import PaymentDialog from '../components/PaymentDialog.tsx';

export default function Dashboard() {
  const { account } = useAccount();
  const capabilities = useCapabilities();
  useDocumentTitle('Your events');
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<AdminSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    accountApi
      .sessions()
      .then((result) => setSessions(result.sessions))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Could not load your events.');
      });
  }, []);

  useEffect(() => {
    if (account) load();
  }, [account, load]);

  if (account === false) return <Navigate to="/signin" replace />;

  return (
    <main className="mx-auto w-full max-w-3xl flex-1 px-6 pb-16">
      <p className="eyebrow">Your events</p>
      <h1 className="font-display mt-3 text-4xl font-semibold">Walls you run</h1>

      {error ? (
        <p role="alert" className="panel mt-6 p-4 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <CreateWall
        onCreated={(result) => navigate('/created', { state: result, replace: false })}
      />

      <section className="mt-10 grid gap-4">
        {sessions === null ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted">
            Nothing here yet. Name a wall above and it appears here.
          </p>
        ) : (
          sessions.map((session) => (
            <WallCard
              key={session.id}
              session={session}
              paymentsEnabled={capabilities?.payments ?? false}
              onChanged={load}
            />
          ))
        )}
      </section>

      <ClaimWall onClaimed={load} />
    </main>
  );
}

function CreateWall({
  onCreated,
}: {
  onCreated: (result: CreateSessionResult & { name: string }) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The name rides along so the next page can talk about the wall by it.
      onCreated({ ...(await accountApi.createSession(name.trim())), name: name.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that wall.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="panel mt-8 grid gap-4 p-5">
      <div className="grid gap-2">
        <label className="eyebrow" htmlFor="new-wall-name">
          Start a new wall
        </label>
        <input
          id="new-wall-name"
          className="field"
          type="text"
          placeholder="Sam and Robin's wedding"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          required
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <button className="btn btn-primary justify-self-start" disabled={busy || !name.trim()}>
        {busy ? 'Creating…' : 'Create wall'}
      </button>
    </form>
  );
}

function WallCard({
  session,
  paymentsEnabled,
  onChanged,
}: {
  session: AdminSession;
  paymentsEnabled: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  // Set when the dialog reports a completed form. The plan only moves when the
  // webhook lands, and this list has no socket, so the card says it is waiting
  // and reloads a few times rather than sitting on a stale "not paid for".
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const terms = PLAN_TERMS[session.plan];
  const needsPaying = session.plan === 'unpaid';

  useEffect(() => {
    if (!confirming) return;
    if (!needsPaying) {
      setConfirming(false);
      return;
    }
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      onChanged();
      if (tries >= 6) {
        window.clearInterval(timer);
        setConfirming(false);
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [confirming, needsPaying, onChanged]);

  async function remove() {
    if (!window.confirm(`Delete "${session.name}" and everything on it? This cannot be undone.`)) {
      return;
    }
    setBusy(true);
    try {
      await accountApi.deleteSession(session.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that wall.');
      setBusy(false);
    }
  }

  return (
    <article className="panel grid gap-3 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-xl font-semibold">{session.name}</h2>
        <span className={`eyebrow ${needsPaying ? 'text-sulphur' : ''}`}>{terms.label}</span>
      </div>

      <p className="text-sm text-muted">
        {session.usage.media} photo{session.usage.media === 1 ? '' : 's'} ·{' '}
        {session.usage.messages} message{session.usage.messages === 1 ? '' : 's'} · until{' '}
        {new Date(session.expiresAt).toLocaleDateString()}
      </p>

      {needsPaying ? (
        <p className="text-sm text-muted">
          {confirming
            ? 'Payment received - waiting for Stripe to confirm it, usually a few seconds.'
            : 'Guests cannot add anything to this wall yet.'}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <a className="btn btn-ghost" href={session.adminUrl}>
          Control panel
        </a>
        <a className="btn btn-ghost" href={session.slideshowUrl} target="_blank" rel="noreferrer">
          Slideshow
        </a>
        {needsPaying && paymentsEnabled ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setPaying(true)}
            disabled={busy || confirming}
          >
            Pay to go live
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost text-danger"
          onClick={remove}
          disabled={busy}
        >
          Delete
        </button>
      </div>

      {paying ? (
        <PaymentDialog
          wallName={session.name}
          start={() => accountApi.checkout(session.id)}
          onPaid={() => {
            setPaying(false);
            setConfirming(true);
            onChanged();
          }}
          onClose={() => setPaying(false)}
        />
      ) : null}
    </article>
  );
}

/**
 * For a wall made before signing up - or before accounts existed at all. The link
 * is what a host actually has to hand, so the whole thing is accepted.
 */
function ClaimWall({ onClaimed }: { onClaimed: () => void }) {
  const [adminUrl, setAdminUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await accountApi.claimSession(adminUrl.trim());
      setAdminUrl('');
      setOpen(false);
      onClaimed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that wall.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="mt-8 text-sm text-petal hover:underline"
        onClick={() => setOpen(true)}
      >
        Have a wall that is not listed here?
      </button>
    );
  }

  return (
    <form onSubmit={onSubmit} className="panel mt-8 grid gap-4 p-5">
      <div className="grid gap-2">
        <label className="eyebrow" htmlFor="claim-url">
          Add an existing wall
        </label>
        <input
          id="claim-url"
          className="field"
          type="text"
          placeholder="https://…/a/your-control-panel-link"
          value={adminUrl}
          onChange={(e) => setAdminUrl(e.target.value)}
          required
          autoFocus
        />
        <p className="text-xs text-muted">
          Paste the control panel link. It only works if the wall has no owner yet.
        </p>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy || !adminUrl.trim()}>
          {busy ? 'Adding…' : 'Add it'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
